import { useEffect, useMemo } from 'react';
import { useGetMyMatch } from '@/services';
import { getUserData } from '@/utils/cookies';
import { clearMatchResultLocally } from '@/utils/matchHandoff';

/** { professionalId: rank } — 1, 2, 3 na ordem do match do usuário. */
export type MatchRanks = Record<string, number>;

/**
 * Profissionais indicados pelo match do usuário LOGADO.
 *
 * A fonte é o servidor (`GET /match/me`), nunca o localStorage: o match é do dono
 * da conta, não do navegador. Deslogado (ou em outra conta) o retorno é vazio, e
 * a chave antiga que ficou no dispositivo é apagada.
 */
export const useMatchedProfessionalRanks = (): MatchRanks => {
	const isLoggedUser = Boolean(getUserData());
	const { data } = useGetMyMatch({ enabled: isLoggedUser, retry: false });

	useEffect(() => {
		// Limpeza do resíduo de versões anteriores, que guardavam o match no browser.
		if (!isLoggedUser) clearMatchResultLocally();
	}, [isLoggedUser]);

	return useMemo(() => {
		if (!isLoggedUser || !data?.answered || !data.result) return {};
		const ranks: MatchRanks = {};
		for (const p of data.result.professionals) ranks[p.id] = p.rank;
		return ranks;
	}, [isLoggedUser, data]);
};

/**
 * Regra de exibição de profissionais: o match do usuário vem primeiro (1º, 2º, 3º)
 * e o resto mantém a ordem que veio da API (a ordem do admin). Sem match — visitante
 * ou quem não respondeu ao questionário — a lista não muda.
 */
export function sortByMatchRank<T extends { id: string }>(
	professionals: T[],
	ranks: MatchRanks,
): T[] {
	if (Object.keys(ranks).length === 0) return professionals;
	return [...professionals].sort((a, b) => {
		const ra = ranks[a.id] ?? Number.POSITIVE_INFINITY;
		const rb = ranks[b.id] ?? Number.POSITIVE_INFINITY;
		return ra - rb;
	});
}
